import {action, makeAutoObservable, observable} from "mobx";
import {TIMELINE_MIN_DURATION} from "../config/config";
import {CompositionService} from "../services/CompositionService";

export interface UserClip {
    id: string;
    type: string;
    startTime: number;
    duration: number;
    trimmedDuration: number;
    hasPredecessor?: boolean;
    hasSuccessor?: boolean;
}

export interface Layer {
    id: string;
    clipIds: string[];
}

export interface Transition {

}

class CompositionStateStoreInstance {
    isPlaying: boolean = false;
    currentTime: number = 0;
    isCompositionEmpty: boolean = true;
    orderedLayerIds: string[] = []; // in reverse order
    duration: number = TIMELINE_MIN_DURATION;
    compositionDuration: number = 0;
    clips: Record<string, UserClip> = {};
    layers: Record<string, Layer> = {};
    updateTimestamp: number = 0;
    transitions: Record<string, Transition> = {};
    display = {
        width: 1920,
        height: 1080,
        backgroundColor: "#000000",
    }
    clipIdToAssetId: Record<string, string> = {}; // Maps a composition clipId to a trackId/assetId (depending on the clip type) on Libretto
    clipIdToMediaResolution: Record<string, {width: number, height: number}> = {};
    clipIdToThumbnailId: Record<string, string> = {}; // Only for images - which have a thumnbailId (distinct from the assetId)
    clipIdToTitle: Record<string, string> = {};
    assetIdToClips: Record<string, string[]> = {};
    constructor() {
        makeAutoObservable(this, {
            setDuration: action,
            setCompositionDuration: action,
            setIsPlaying: action,
            setCurrentTime: action,
            setIsCompositionEmpty: action,
            updateTimelineDuration: action,
            setOrderedLayerIds: action,
            setUpdateTimestamp: action,
            setDisplayResolution: action,
            orderedLayerIds: observable,
            display: observable,
        });
    }

    setIsCompositionEmpty(empty: boolean) {
        console.log("setIsCompositionEmpty", empty);
        this.isCompositionEmpty = empty;
    }

    setOrderedLayerIds(orderedLayerIds: string[]) {
        this.orderedLayerIds = orderedLayerIds;
    }

    setDuration(duration: number) {
        this.duration = duration;
    }

    setCompositionDuration(duration: number) {
        this.compositionDuration = duration;
    }

    updateTimelineDuration() {
        const compositionDuration = CompositionService.getFitDuration();
        this.setCompositionDuration(compositionDuration);
        const duration = Math.max(TIMELINE_MIN_DURATION, compositionDuration + 10);
        this.setDuration(duration);
    }

    getClipTitle(clipId: string): string | undefined {
        const type = String(CompositionStateStore.clips?.[clipId]?.type ?? "").toLowerCase();
        // Non-media clip titles are fixed (ignore any internal names like "__libretto_layout__...").
        if (type === "placeholder") return "Placeholder";
        if (type === "text") return "Text";
        if (type === "captions") return "Captions";

        const explicit = CompositionStateStore.clipIdToTitle[clipId];
        if (explicit != null && explicit !== "") return explicit;
        if (type === "audio") return "Audio";
        if (type === "video") return "Video";
        if (type === "image") return "Image";
        if (type === "script") return "Script";

        return undefined;
    }

    getThumbnailByClipId(clipId: string) {
        return "clip-thumbnail"
    }

    addLayer(layer: Layer) {
        this.layers[layer.id] = layer;
        this.orderedLayerIds.push(layer.id);
    }

    removeLayer(layerId: string) {
        const layer = this.layers[layerId];

        if (layer == null) {
            return;
        }

        this.orderedLayerIds = this.orderedLayerIds.filter(id => id !== layerId);
        delete this.layers[layerId];
    }

    addClip(layerId: string, userClip: UserClip) {
        this.clips[userClip.id] = userClip;

        if (this.layers[layerId]) {
            this.layers[layerId].clipIds.push(userClip.id);
        } else {
            this.layers[layerId] = { id: layerId, clipIds: [userClip.id] };
            this.orderedLayerIds.push(layerId);
        }
    }

    updateClip(clipId: string, userClip: UserClip) {
        this.clips[clipId] = userClip;
    }

    removeClip(layerId: string, clipId: string) {
        if (this.layers[layerId]) {
            this.layers[layerId].clipIds = this.layers[layerId].clipIds.filter(id => id !== clipId);
        }
        delete this.clips[clipId];
    }

    setDisplayBackgroundColor(color: string) {
        this.display.backgroundColor = color;
    }

    setDisplayResolution(width: number, height: number) {
        this.display.width = width;
        this.display.height = height;

        console.log("Display resolution updated to: ", this.display)
    }

    setIsPlaying(isPlaying: boolean) {
        this.isPlaying = isPlaying;
    }

    setCurrentTime(currentTime: number) {
        this.currentTime = currentTime;
    }

    setUpdateTimestamp(timestamp: number) {
        this.updateTimestamp = timestamp;
    }

    setAssetIdForClip(clipId: string, assetId: string) {
        const id = String(clipId ?? "");
        const nextAssetId = String(assetId ?? "");
        if (!id || !nextAssetId) return;

        const previousAssetId = this.clipIdToAssetId[id];
        if (
            previousAssetId &&
            previousAssetId !== nextAssetId &&
            Array.isArray(this.assetIdToClips[previousAssetId])
        ) {
            this.assetIdToClips[previousAssetId] = this.assetIdToClips[
                previousAssetId
            ].filter((candidateId) => candidateId !== id);
            if (this.assetIdToClips[previousAssetId].length === 0) {
                delete this.assetIdToClips[previousAssetId];
            }
        }

        this.clipIdToAssetId[id] = nextAssetId;
        if (!this.assetIdToClips[nextAssetId]) {
            this.assetIdToClips[nextAssetId] = [];
        }
        if (!this.assetIdToClips[nextAssetId].includes(id)) {
            this.assetIdToClips[nextAssetId].push(id);
        }
    }

    setThumbnailIdForClip(clipId: string, thumbnailId: string) {
        this.clipIdToThumbnailId[clipId] = thumbnailId;
    }

    setOriginalMediaResolutionForClip(clipId: string, width: number, height: number) {
        this.clipIdToMediaResolution[clipId] = {width, height};
    }

    setTitleForClip(clipId: string, filename: string) {
        this.clipIdToTitle[clipId] = filename;
    }

    removeMetadataForClip(clipId: string) {
        const id = String(clipId ?? "");
        if (!id) return;
        const assetId = this.clipIdToAssetId[id];
        delete this.clipIdToAssetId[id];
        delete this.clipIdToMediaResolution[id];
        delete this.clipIdToThumbnailId[id];
        delete this.clipIdToTitle[id];
        if (assetId && Array.isArray(this.assetIdToClips[assetId])) {
            this.assetIdToClips[assetId] = this.assetIdToClips[assetId].filter(
                (candidateId) => candidateId !== id,
            );
            if (this.assetIdToClips[assetId].length === 0) {
                delete this.assetIdToClips[assetId];
            }
        }
    }

    reset() {
        this.isPlaying = false;
        this.currentTime = 0;
        this.isCompositionEmpty = true;
        this.orderedLayerIds = [];
        this.duration = TIMELINE_MIN_DURATION;
        this.compositionDuration = 0;
        this.clips = {};
        this.layers = {};
        this.updateTimestamp = 0;
        this.transitions = {};
        this.display = {
            width: 1920,
            height: 1080,
            backgroundColor: "#000000",
        };
        this.clipIdToAssetId = {};
        this.clipIdToMediaResolution = {};
        this.clipIdToThumbnailId = {};
        this.clipIdToTitle = {};
        this.assetIdToClips = {};
    }
}

const CompositionStateStore = new CompositionStateStoreInstance();

export { CompositionStateStore };
